<template>
  <HgLayout
    :class="{ 'map-view': isMapView }"
    :baseUrl="baseUrl"
    :currentLanguage="locale"
    :currentRoute="route"
    hideFooter
    :getLanguageToLink="getLanguageToLink"
    :isTopNavEnabled="isNewNavigationEnabled"
    :isUserLoggedIn="isLoggedIn"
    @trackFooterClick="handleTrackFooterClick"
    @trackHeaderClick="handleTrackHeaderClick"
  >
    <template #headerMetaAction>
      <HgMainNavItem
        :menuItem="adMenuItem"
        :currentRoute="route"
        :baseUrl="baseUrl"
        :bordered="true"
        @click="handleTrackNavItemClick('open_insertAd', 'tab_navigation')"
      />
    </template>
    <template #headerMetaNavigation>
      <FavouritesLink
        :menuItem="favouritesMenuItem"
        :baseUrl="baseUrl"
        :menuNavItem="HgMainNavItem"
        :platform="SupportedPlatforms.hg"
        :isProduction="isProd"
        :authToken="authToken"
        :route="route"
        @click="handleTrackNavItemClick('open_favourites', 'tab_navigation')"
      />
      <HgMainNavItem
        :menuItem="mainHeaderMenuItem"
        icon="PersonOutline"
        :baseUrl="baseUrl"
        :currentRoute="route"
        dropdownOrientation="right"
        @click="handleTrackNavItemClick('open_login', 'tab_navigation')"
      />
    </template>
    <template #sideMenuMetaNavigation>
      <FavouritesLink
        :menuItem="favouritesMenuItem"
        :baseUrl="baseUrl"
        :menuNavItem="HgSideMenuNavItem"
        :platform="SupportedPlatforms.hg"
        :isProduction="isProd"
        :authToken="authToken"
        :route="route"
        @click="handleTrackNavItemClick('open_favourites', 'tab_navigation')"
      />
    </template>
    <template #sideMenuAccountNavigation>
      <template v-if="isLoggedIn">
        <HgSideMenuNavItem
          :menuItem="tm('$hg.nav.harmonize.overviewMenuItem') as unknown as MenuItem"
          icon="PersonOutline"
          :baseUrl="accountBaseUrl"
          :currentRoute="route"
        />
        <HgSideMenuNavItem
          :menuItem="tm('$hg.nav.harmonize.settingsMenuItem') as unknown as MenuItem"
          icon="Settings"
          :baseUrl="accountBaseUrl"
          :currentRoute="route"
        />
        <HgSideMenuNavItem
          :menuItem="tm('$hg.nav.harmonize.myAdsMenuItem') as unknown as MenuItem"
          icon="Appointment"
          :baseUrl="baseUrl"
          :currentRoute="route"
        />
        <HgSideMenuNavItem
          :menuItem="tm('$hg.nav.harmonize.myRealEstateMenuItem') as unknown as MenuItem"
          icon="House"
          :baseUrl="baseUrl"
          :currentRoute="route"
        />
        <HgSideMenuNavItem
          :menuItem="tm('$hg.nav.harmonize.searchAlertMenuItem') as unknown as MenuItem"
          icon="Bell"
          :baseUrl="baseUrl"
          :currentRoute="route"
        />
        <HgSideMenuNavItem
          :menuItem="tm('$hg.nav.harmonize.tenantPlusMenuItem') as unknown as MenuItem"
          icon="PlusMembership"
          :baseUrl="baseUrl"
          :currentRoute="route"
        />
        <HgSideMenuNavItem
          :menuItem="tm('$hg.nav.harmonize.signOutMenuItem') as unknown as MenuItem"
          icon="SignOut"
          :baseUrl="accountBaseUrl"
          :currentRoute="route"
        />
      </template>
    </template>
    <router-view />
  </HgLayout>
</template>

<script lang="ts" setup>
import {
  HgLayout,
  HgMainNavItem,
  HgSideMenuNavItem,
  SupportedPlatforms,
  type HgSupportedLanguage,
  type MenuItem,
  type TrackingData,
  type TrackingHeaderData,
} from '@smg-real-estate/domus';
import { setGeoData } from '@smg-real-estate/geo-libs';
import { FavouritesLink } from '@smg-real-estate/seeker-app-shared-components';
import { useHead } from '@unhead/vue';
import { storeToRefs } from 'pinia';
import { computed, onMounted, onServerPrefetch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import { fetchSmgSplitSession, setTemporaryUUIDCookie } from '@/api/splitApi';
import useConditionalSSRContext from '@/composables/useConditionalSSRContext';
import { getAuthToken } from '@/helpers/auth/authClient';
import { getAppMetaInfo } from '@/helpers/metaInfoHelper';
import { getLanguageSwitchLink } from '@/router';
import useGeoStore from '@/stores/useGeoStore';
import useMapStore from '@/stores/useMapStore';
import useSplitStore from '@/stores/useSplitStore';
import { trackFooter, trackHeader } from '@/tracking';
import { ViewMode } from '@/types';
import { SplitFeatureFlags } from './stores/splitFeatureFlags';

const splitStore = useSplitStore();
const route = useRoute();
const { viewMode } = storeToRefs(useMapStore());
const { getUrlByGeoLocation } = storeToRefs(useGeoStore());
const { splitSessionKey } = storeToRefs(splitStore);
const { tm, locale } = useI18n();
const ctx = useConditionalSSRContext();

const authToken = ref<string | undefined>();
const isLoggedIn = computed(() => authToken.value !== undefined);

onMounted(async () => {
  window.addEventListener('optanonHandler', async () => {
    setTemporaryUUIDCookie(splitSessionKey.value, document.cookie, window.location.href);

    const smgSplitSessions = await fetchSmgSplitSession();
    splitStore.updateSplitStoreFromApi(smgSplitSessions);
  });
  authToken.value = await getAuthToken();
});

const baseUrl = ref(import.meta.env.VUE_PLATFORM_BASE_URL);
const accountBaseUrl = import.meta.env.VUE_PLATFORM_ACCOUNT_BASE_URL as string;

const isMapView = computed((): boolean => {
  return viewMode.value === ViewMode.Map;
});

const adMenuItem = computed((): MenuItem => {
  return tm(
    isNewNavigationEnabled.value
      ? '$hg.nav.topNavNewListingMenuItem'
      : '$hg.nav.harmonize.newListingMenuItem',
  ) as unknown as MenuItem;
});

const userAccountMenuItem = computed((): MenuItem => {
  return {
    label: tm('$hg.nav.harmonize.overviewMenuItem.label') as string,
    href: `${accountBaseUrl}/${locale.value}/overview`,
  };
});

const mainHeaderMenuItem = computed((): MenuItem => {
  if (isLoggedIn.value) {
    let privateItems = JSON.parse(JSON.stringify(tm('$hg.nav.privateItems')));
    if (Array.isArray(privateItems)) {
      privateItems = privateItems.map(
        (item: { href: string; trackingLabel: string; [key: string]: unknown }) => {
          const modifiedHref =
            item.href.includes('messenger') ||
            item.href.includes('my-ads') ||
            item.trackingLabel.includes('searchAlertSettings') ||
            item.href.includes('my-realestate')
              ? `${baseUrl.value}${item.href}`
              : `${accountBaseUrl}${item.href}`;
          return { ...item, href: modifiedHref };
        },
      );
    }
    return {
      label: tm('$hg.nav.loggedInMenuItem.label') as string,
      children: privateItems,
    };
  }
  return userAccountMenuItem.value;
});

const favouritesMenuItem = computed((): MenuItem => {
  return tm('$hg.nav.favoriteMenuItem') as unknown as MenuItem;
});

const getLanguageToLink = (language: HgSupportedLanguage) => {
  if (typeof route.meta?.getLanguageSwitchLink === 'function') {
    return route.meta.getLanguageSwitchLink(route, language, getUrlByGeoLocation.value[language]);
  }
  return getLanguageSwitchLink(route, language);
};

const isNewNavigationEnabled = computed(() =>
  splitStore.isFeatureOn(SplitFeatureFlags.DD4533_NEW_TOP_LEVEL_NAVIGATION),
);

const isProd = computed((): boolean => import.meta.env.VUE_STAGE === 'prod');

const handleTrackHeaderClick = (data: {
  eventAction: string;
  eventLabel: string;
  contentLanguage?: string;
}) => {
  trackHeader({
    eventAction: data.eventAction,
    eventLabel: data.eventLabel,
    contentLanguage: data.contentLanguage ?? locale.value,
  });
};

const handleTrackFooterClick = (data: TrackingData) => {
  trackFooter(data);
};

const handleTrackNavItemClick = (action: string, label: string) => {
  const trackingData: TrackingHeaderData = {
    eventAction: action,
    eventLabel: label,
    contentLanguage: locale.value,
  };
  trackHeader(trackingData);
};

useHead(() => {
  return getAppMetaInfo(locale.value as HgSupportedLanguage);
});

onServerPrefetch(async () => {
  splitStore.updateSplitStoreFromApi(ctx!.smgSplitSession);
  // Set geo-data from layer added by the lambda
  setGeoData(ctx!.geoData);
});
</script>
<style lang="scss">
@use '@/styles/global-hg' as *;
</style>

<style lang="scss" scoped>
@use '@/styles/variables' as *;

.map-view {
  min-height: auto;
}
</style>

<!--TODO: fix the modal style issue on mobile when data layer is expanded, can be fixed if modal supports to insert to body-->
<style lang="scss">
@use '@/styles/variables' as *;

.hg-modal-open {
  div[class*='DataViewLayer_expanded'] {
    overflow: hidden;
  }

  div[class*='MapHeaderLayer_container'] {
    z-index: 1;
  }

  &.search-modal-open {
    div[class*='MapHeaderLayer_container'] {
      z-index: $hg-mobile-search-header-zindex;
    }
  }
}
</style>
